import { z } from 'zod';

/**
 * How an ONU is actually managed, as opposed to how this platform meant to
 * manage it.
 *
 * `onus.management_mode` records an INTENT — it is written when SimpleACS
 * performs a mode change and never reconciled against the chassis. On this
 * fleet that made it wrong for 159 of 4,697 ONUs: 119 informing an ACS while
 * the column said nothing, and 40 the column called `tr069` that have never
 * informed anything. An intent standing in for a fact is fine until something
 * branches on it, and `device-presets` does.
 *
 * So this is derived from what the CHASSIS reports — the `ACS:` line in
 * `show gpon remote-onu tr069-mgmt` — against the ACS servers the ONU's own
 * organisation actually runs.
 *
 *   omci         no ACS configured, or TR-069 locked. OMCI is the only path.
 *   tr069        informing an ACS this organisation owns. Fully manageable.
 *   tr069_other  configured for TR-069, pointed SOMEWHERE ELSE.
 *
 * The third is the one worth having a word for. Such an ONU looks managed and
 * is not: it will never appear in our ACS, every TR-069 control on it is inert,
 * and the only thing that reaches it is OMCI through its OLT. Before this it
 * was indistinguishable from `omci` in the column and from `tr069` in the
 * chassis config, which is exactly how an operator ends up sending a CPE
 * setting to a device that is somebody else's to configure.
 */
/**
 * Whether an ONU's WiFi belongs to its router — written over TR-069 and
 * recorded by SimpleACS — rather than to the OLT (operator, 2026-10-05: "I
 * thought when we manage with tr069 all settings go to the DB and genieacs,
 * why are they going to OLT"). The operator's mode decides: TR-069 is the
 * router's, OMCI is the OLT's. With no mode recorded, a router that has
 * informed this ACS decides. Every path that would write WiFi through the
 * OLT asks this first.
 */
export function wifiOwnedByRouter(onu: {
  managementMode?: string | null;
  deviceId?: string | null;
}): boolean {
  if (onu.managementMode === 'tr069') return true;
  if (onu.managementMode === 'omci') return false;
  return Boolean(onu.deviceId);
}

export const ONU_MANAGEMENT_STATES = ['omci', 'tr069', 'tr069_other'] as const;
export const onuManagementStateSchema = z.enum(ONU_MANAGEMENT_STATES);
export type OnuManagementState = (typeof ONU_MANAGEMENT_STATES)[number];

/**
 * Two ACS addresses are the same server when their host and port agree.
 *
 * The PATH is deliberately not part of the comparison. SmartOLT provisions this
 * fleet's ONUs with `http://10.79.0.1:7547/acs` and SimpleACS's own profile is
 * `http://10.79.0.1:7547`; GenieACS accepts any path and an ONU on the bare URL
 * informs perfectly well, so treating those as different servers would report
 * 179 of one chassis's ONUs as belonging to a stranger.
 *
 * A default port is filled in per scheme so `http://host` and `http://host:80`
 * are not two servers either.
 */
export function sameAcsServer(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  const left = acsOrigin(a);
  const right = acsOrigin(b);
  return left !== null && left === right;
}

/**
 * `scheme://host:port`, or null if this is not an address at all.
 *
 * Parsed by hand rather than with `URL`, which this package cannot reach: the
 * shared types build without DOM or Node libs so that both the API and the
 * browser can import them, and reaching for a runtime global here would tie
 * them to one of the two.
 */
function acsOrigin(raw: string): string | null {
  const m = /^\s*([A-Za-z][A-Za-z0-9+.-]*):\/\/([^/:?#\s]+)(?::(\d+))?/.exec(raw);
  if (!m) return null;
  const scheme = m[1]!.toLowerCase();
  const host = m[2]!.toLowerCase();
  const port = m[3] ?? (scheme === 'https' ? '443' : scheme === 'http' ? '80' : '');
  return `${scheme}://${host}:${port}`;
}

/**
 * Classify one ONU from the chassis's own answer.
 *
 * `acsUrl` is what the chassis reports the ONU is pointed at; `ownAcsUrls` are
 * the ACS addresses this ONU's organisation runs. `unlocked` is the ONU's
 * TR-069 admin state — a locked client does not inform whatever its URL says,
 * which is precisely what switching an ONU to OMCI does (`tr069-mgmt … state
 * lock`), so a locked ONU is `omci` even though its ACS line survives.
 *
 * `null` for `acsUrl` means the chassis reported none. `unlocked` of null means
 * the read did not say, which is treated as unlocked rather than assumed: an
 * ONU with an ACS configured is more usefully described by WHICH ACS than by a
 * guess about a field that was not in the output.
 */
export function classifyOnuManagement(input: {
  acsUrl: string | null | undefined;
  unlocked?: boolean | null;
  ownAcsUrls: readonly string[];
}): OnuManagementState {
  const url = input.acsUrl?.trim() || null;
  if (!url) return 'omci';
  if (input.unlocked === false) return 'omci';
  return input.ownAcsUrls.some((own) => sameAcsServer(url, own)) ? 'tr069' : 'tr069_other';
}

/** What to call each state on screen. */
export const ONU_MANAGEMENT_LABELS: Record<OnuManagementState, string> = {
  omci: 'OMCI',
  tr069: 'TR-069',
  tr069_other: 'TR-069 (other)',
};

/**
 * Whether an ONU that was PUT on TR-069 is actually talking to the ACS.
 *
 * These are two different facts and the platform has always held both, without
 * ever putting them side by side. `managementState` is what the CHASSIS says —
 * the `tr069-mgmt` line names our ACS — and it is satisfied the moment that
 * line is written. Whether the CPE then went and informed is a different
 * question, answered by whether an ACS device row is linked to it at all.
 *
 * An ONU that is `tr069` with nothing linked has been configured and has not
 * been heard from. That is not a rare state to be tidied away: on this fleet it
 * is how a management VLAN that does not reach the ONU looks, and how a site
 * router that has not been told to forward the management scope looks, and it
 * is invisible on both screens that could have shown it — the list says
 * "TR-069" and the detail page says "TR-069", because both are reporting the
 * chassis honestly.
 *
 *   `silent`    — configured for our ACS, never informed. Somebody must go fix
 *                 something; see TR069_SILENCE_CAUSES.
 *   `stale`     — informed once and then stopped. The path worked, so this is a
 *                 different fault from `silent` and gets a different first
 *                 question.
 *   `informing` — configured and talking. Nothing to do.
 *   `null`      — the question does not apply: OMCI-managed, on somebody else's
 *                 ACS, or the chassis has not been read yet, and an unread ONU
 *                 must never be reported as silent (that is the mistake that
 *                 once had 119 ONUs recorded as not informing an ACS they were
 *                 informing).
 */
export type Tr069Reachability = 'silent' | 'stale' | 'informing';

export function tr069Reachability(input: {
  /** Undefined when the chassis has not been read — see the note above. */
  managementState: OnuManagementState | undefined | null;
  /** Null when no ACS device has ever been linked to this ONU. */
  lastInformAt: number | null | undefined;
  /** The operator's own online window, from Settings → Timings. */
  onlineWindowMs: number;
  now: number;
}): Tr069Reachability | null {
  if (input.managementState !== 'tr069') return null;
  if (input.lastInformAt === null || input.lastInformAt === undefined) return 'silent';
  return input.now - input.lastInformAt <= input.onlineWindowMs ? 'informing' : 'stale';
}

/**
 * The OLT config and the ACS disagree: the CPE is informing OUR ACS right now,
 * so the ONU is on our TR-069 in fact, but a chassis read found no ACS line and
 * classified it OMCI.
 *
 * This is the bridged-subscriber shape. A bridge informs the ACS over its own
 * WAN, not the OLT-delivered TR-069 path, so the OLT's `pon-onu-mng` carries no
 * `tr069-mgmt … acs …` line and the per-ONU read comes back with no ACS — while
 * the CPE is calling the ACS every few minutes. Reporting it as OMCI is how a
 * real TR-069 subscriber goes missing from the fleet's TR-069 view; reporting it
 * as plain TR-069 would hide that the OLT's own config does not agree. So it is
 * its OWN state: TR-069 in fact, OLT config to be corrected.
 *
 * Requires `managementState === 'omci'` (a chassis read that FOUND no ACS), not
 * merely an unread ONU — the same reason the reachability states do: the
 * platform must have looked before it says the OLT is wrong.
 */
export function oltAcsMismatch(input: {
  /** The chassis-derived state; `omci` here means a read found no ACS line. */
  managementState: OnuManagementState | undefined | null;
  /** The linked ACS device, or null when none has ever informed. */
  deviceId: string | null | undefined;
  /** When that device last informed our ACS. */
  lastInformAt: number | null | undefined;
  onlineWindowMs: number;
  now: number;
}): boolean {
  const informingNow =
    input.deviceId != null &&
    input.lastInformAt != null &&
    input.now - input.lastInformAt <= input.onlineWindowMs;
  return informingNow && input.managementState === 'omci';
}

/**
 * How long "it has not informed yet" is still a reasonable thing to say.
 *
 * An ONU informs on its own schedule, so between writing TR-069 to the chassis
 * and the CPE calling the ACS there is a genuine gap, and telling somebody to
 * go and check a management VLAN during it would send them after a fault that
 * does not exist. One hour is a generous periodic-inform interval: the common
 * configured values are five to sixty minutes, and being late to accuse is the
 * right way to be wrong here.
 *
 * Past it, "waiting" stops being true and becomes an excuse. The banner that
 * said so had no time bound at all, so it told operators "nothing more to do
 * here" for ever about precisely the ONUs they could not find.
 */
export const TR069_INFORM_GRACE_MS = 60 * 60_000;

/**
 * What to go and look at, in the order worth looking.
 *
 * The point of naming the state is being able to act on it, and an operator
 * standing in front of a silent ONU wants the shortlist rather than a status.
 * This order is the fleet's own history: the management VLAN not reaching the
 * unit is by far the most common, and it is the one that looks like nothing is
 * wrong from the chassis side.
 */
export const TR069_SILENCE_CAUSES: readonly string[] = [
  'The management VLAN does not reach this ONU — check that the site router forwards the management scope, and that the ONU has an address on it.',
  'The ONU has not re-ranged since the configuration was written; a reboot or a resync applies it.',
  'The ACS URL or the inform credentials on the chassis do not match the profile this organization uses.',
  'The unit is offline, so nothing has been able to inform.',
];

/** The same shortlist, for an ONU that informed once and then stopped. */
export const TR069_STALE_CAUSES: readonly string[] = [
  'The path worked once, so start with what changed: the ONU going offline, or losing its management address on a lease renewal.',
  'A configuration rewrite that dropped the management VLAN, the GEM port or the IP host.',
  'The ACS moved, or the inform credentials were rotated without the chassis being updated.',
];
